import RytmWebflow from 'rytm-webflow'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'

interface MarkerData {
  lat: number
  lng: number
}

const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'poi.park', elementType: 'labels.text.stroke', stylers: [{ color: '#1b1b1b' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
]

const PLACEMARK_ICON = '/local/images/placemark.png'

class MapController extends RytmWebflow.Controller {
  private map: google.maps.Map | null = null
  private mapMarkers: google.maps.Marker[] = []
  private loading = false

  addEventListeners() {
    super.addEventListeners()
    this.initMap()
  }

  removeEventListeners() {
    super.removeEventListeners()
    this.destroyMap()
  }

  private async initMap() {
    const canvas = document.getElementById('contact-map-canvas') as HTMLDivElement | null
    if (!canvas) return

    const apiKey = canvas.dataset.apiKey ?? ''
    const markers: MarkerData[] = JSON.parse(canvas.dataset.markers ?? '[]')

    this.loading = true
    setOptions({ key: apiKey, v: 'weekly' })
    const { Map } = await importLibrary('maps')
    const { Marker } = await importLibrary('marker')

    if (!this.loading) return // navigated away before load completed

    const center = markers[0] ?? { lat: 52.2297, lng: 21.0122 }
    this.map = new Map(canvas, { center, zoom: 16, styles: MAP_STYLES })

    this.mapMarkers = markers.map(pos => new Marker({
      map: this.map!,
      position: pos,
      icon: PLACEMARK_ICON,
    }))

    if (markers.length > 1) {
      const { LatLngBounds, event } = await importLibrary('core')
      const bounds = new LatLngBounds()
      markers.forEach(pos => bounds.extend(pos))
      this.map.fitBounds(bounds)
      event.addListenerOnce(this.map, 'idle', () => {
        this.map!.setZoom(this.map!.getZoom()! - 2)
      })
    }
    this.loading = false
  }

  private destroyMap() {
    this.loading = false
    this.mapMarkers.forEach(m => m.setMap(null))
    this.mapMarkers = []
    this.map = null
  }
}

export default MapController
